import React from 'react';
import { Link } from 'react-router';
import './Pagination.css';

interface PaginationBaseProps {
  currentPage: number;
  totalPages: number;
  maxPageButtons?: number;
  showFirstLast?: boolean;
  className?: string;
}

/** Button mode: the page lives in component state and the caller receives clicks. */
interface PaginationButtonProps extends PaginationBaseProps {
  onPageChange: (page: number) => void;
  getPageHref?: never;
}

/**
 * Anchor mode: the page lives in the URL. Every control that leads somewhere is a
 * real <a href>, so crawlers following the SPA fallback and keyboard users both
 * reach the next page, and back navigation walks pages. The current page and the
 * edges (Previous on page 1, Next on the last page) are non-links.
 */
interface PaginationAnchorProps extends PaginationBaseProps {
  getPageHref: (page: number) => string;
  onPageChange?: never;
}

export type PaginationProps = PaginationButtonProps | PaginationAnchorProps;

/**
 * Reusable Pagination Component
 *
 * Displays pagination controls with smart truncation for large page counts.
 * Pattern: First | Previous | 1 2 3 ... N | Next | Last
 *
 * @param currentPage - Current active page (1-indexed)
 * @param totalPages - Total number of pages
 * @param onPageChange - Callback when page changes (button mode)
 * @param getPageHref - Href builder for each page (anchor mode)
 * @param maxPageButtons - Maximum page number buttons to show (default: 5)
 * @param showFirstLast - Show First/Last buttons (default: true)
 * @param className - Additional CSS class names
 */
const Pagination: React.FC<PaginationProps> = (props) => {
  const {
    currentPage,
    totalPages,
    showFirstLast = true,
    className = ''
  } = props;

  // Validate inputs
  if (totalPages <= 0) return null;
  if (currentPage < 1 || currentPage > totalPages) return null;

  /**
   * Generate array of page numbers to display with smart truncation
   * Examples:
   * - Total 7 pages: [1, 2, 3, 4, 5, 6, 7]
   * - Total 30, current 3: [1, 2, 3, 4, 5, '...', 30]
   * - Total 30, current 15: [1, '...', 13, 14, 15, 16, 17, '...', 30]
   * - Total 30, current 28: [1, '...', 26, 27, 28, 29, 30]
   */
  const getPageNumbers = (): (number | string)[] => {
    // If total pages <= 7, show all pages
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const showEllipsisStart = currentPage > 4;
    const showEllipsisEnd = currentPage < totalPages - 3;

    // Always show first page
    pages.push(1);

    // Current page is near the start (pages 1-4)
    if (!showEllipsisStart) {
      for (let i = 2; i <= Math.min(5, totalPages - 1); i++) {
        pages.push(i);
      }
      if (totalPages > 6) {
        pages.push('ellipsis-end');
      }
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    // Current page is near the end (last 4 pages)
    else if (!showEllipsisEnd) {
      pages.push('ellipsis-start');
      for (let i = totalPages - 4; i < totalPages; i++) {
        pages.push(i);
      }
      pages.push(totalPages);
    }
    // Current page is in the middle
    else {
      pages.push('ellipsis-start');

      // Show currentPage with 2 neighbors on each side
      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      pages.push('ellipsis-end');
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const renderEllipsis = (key: string) => (
    <span key={key} className="pagination-ellipsis" aria-hidden="true">
      ...
    </span>
  );

  // ---- Anchor mode ----
  if (props.getPageHref) {
    const { getPageHref } = props;
    const isFirst = currentPage === 1;
    const isLast = currentPage === totalPages;

    const edge = (
      extraClass: string,
      label: string,
      targetPage: number,
      disabled: boolean,
      ariaLabel: string
    ) =>
      disabled ? (
        <span
          className={`pagination-btn ${extraClass} is-disabled`}
          aria-disabled="true"
          aria-label={ariaLabel}
        >
          {label}
        </span>
      ) : (
        <Link
          to={getPageHref(targetPage)}
          className={`pagination-btn ${extraClass}`}
          aria-label={ariaLabel}
        >
          {label}
        </Link>
      );

    return (
      <nav className={`pagination ${className}`} aria-label="Pagination">
        {showFirstLast && edge('pagination-first', 'First', 1, isFirst, 'Go to first page')}
        {edge('pagination-prev', 'Previous', currentPage - 1, isFirst, 'Go to previous page')}

        <div className="pagination-numbers">
          {pageNumbers.map((page, index) => {
            if (typeof page === 'string') return renderEllipsis(`${page}-${index}`);
            if (page === currentPage) {
              return (
                <span
                  key={page}
                  className="pagination-btn pagination-number active"
                  aria-current="page"
                  aria-label={`Page ${page}`}
                >
                  {page}
                </span>
              );
            }
            return (
              <Link
                key={page}
                to={getPageHref(page)}
                className="pagination-btn pagination-number"
                aria-label={`Go to page ${page}`}
              >
                {page}
              </Link>
            );
          })}
        </div>

        {edge('pagination-next', 'Next', currentPage + 1, isLast, 'Go to next page')}
        {showFirstLast && edge('pagination-last', 'Last', totalPages, isLast, 'Go to last page')}
      </nav>
    );
  }

  // ---- Button mode (unchanged behavior) ----
  const { onPageChange } = props;

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirst = () => {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  };

  const handleLast = () => {
    if (currentPage !== totalPages) {
      onPageChange(totalPages);
    }
  };

  return (
    <div className={`pagination ${className}`} role="navigation" aria-label="Pagination">
      {/* First Button */}
      {showFirstLast && (
        <button
          className="pagination-btn pagination-first"
          onClick={handleFirst}
          disabled={currentPage === 1}
          aria-label="Go to first page"
          aria-disabled={currentPage === 1}
          style={{ borderRadius: '8px' }}
        >
          First
        </button>
      )}

      {/* Previous Button */}
      <button
        className="pagination-btn pagination-prev"
        onClick={handlePrevious}
        disabled={currentPage === 1}
        aria-label="Go to previous page"
        aria-disabled={currentPage === 1}
        style={{ borderRadius: '8px' }}
      >
        Previous
      </button>

      {/* Page Number Buttons */}
      <div className="pagination-numbers">
        {pageNumbers.map((page, index) => {
          if (typeof page === 'string') return renderEllipsis(`${page}-${index}`);

          // Page number button
          const isActive = page === currentPage;
          return (
            <button
              key={page}
              className={`pagination-btn pagination-number ${isActive ? 'active' : ''}`}
              onClick={() => handlePageClick(page)}
              disabled={isActive}
              aria-label={`Go to page ${page}`}
              aria-current={isActive ? 'page' : undefined}
              aria-disabled={isActive}
              style={{ borderRadius: '8px' }}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        className="pagination-btn pagination-next"
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="Go to next page"
        aria-disabled={currentPage === totalPages}
        style={{ borderRadius: '8px' }}
      >
        Next
      </button>

      {/* Last Button */}
      {showFirstLast && (
        <button
          className="pagination-btn pagination-last"
          onClick={handleLast}
          disabled={currentPage === totalPages}
          aria-label="Go to last page"
          aria-disabled={currentPage === totalPages}
          style={{ borderRadius: '8px' }}
        >
          Last
        </button>
      )}
    </div>
  );
};

export default Pagination;
